import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronRight,
  FileCode,
  FileDown,
  FileText,
  Trash2,
  Upload,
} from "lucide-react";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FacturaExtranjeraForm } from "@/components/FacturaExtranjeraForm";

import {
  borrarFacturaRecibida,
  descargarFacturaRecibidaArchivo,
  descargarFacturaRecibidaFsp,
  getFacturaRecibidaConceptos,
  getFacturasRecibidas,
  marcarPagadaFacturaRecibida,
  programarPagoFacturaRecibida,
  subirFacturaRecibida,
  type FacturaRecibidaItem,
} from "@/lib/api";

// Los NUMERIC de Postgres llegan como string en el JSON; Number() antes de formatear.
const money = (n: number | string | null, moneda: string | null = "MXN") =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: /^[A-Z]{3}$/.test(moneda ?? "") ? (moneda as string) : "MXN",
      }).format(Number(n));
const num = (n: number | string | null, d = 2) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("es-MX", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(Number(n));
const fechaCorta = (s: string | null) => (s ? s.slice(0, 10) : "—");

export function FacturasRecibidasPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["facturas-recibidas"], queryFn: getFacturasRecibidas });
  const facturas = q.data?.items ?? [];

  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [busqueda, setBusqueda] = useState(searchParams.get("q") ?? "");   // búsqueda global
  const [modo, setModo] = useState<"cfdi" | "extranjera">("cfdi");

  // Filtros por columna (además de la búsqueda libre). Las fechas comparan el
  // prefijo yyyy-mm-dd; la de pago usa la real si ya se pagó, si no la programada.
  const [fEmiDesde, setFEmiDesde] = useState("");
  const [fEmiHasta, setFEmiHasta] = useState("");
  const [fEmisor, setFEmisor] = useState("");
  const [fSerieFolio, setFSerieFolio] = useState("");
  const [fMoneda, setFMoneda] = useState("");
  const [fPagoDesde, setFPagoDesde] = useState("");
  const [fPagoHasta, setFPagoHasta] = useState("");
  const hayFiltros = !!(fEmiDesde || fEmiHasta || fEmisor || fSerieFolio || fMoneda || fPagoDesde || fPagoHasta);
  const limpiarFiltros = () => {
    setFEmiDesde(""); setFEmiHasta(""); setFEmisor(""); setFSerieFolio("");
    setFMoneda(""); setFPagoDesde(""); setFPagoHasta("");
  };
  const emisores = useMemo(
    () => [...new Set(facturas.map((f) => f.emisor_nombre).filter(Boolean) as string[])]
      .sort((a, b) => a.localeCompare(b, "es")),
    [facturas],
  );
  const monedas = useMemo(
    () => [...new Set(facturas.map((f) => f.moneda).filter(Boolean) as string[])].sort(),
    [facturas],
  );

  const visibles = useMemo(() => {
    const b = busqueda.trim().toLowerCase();
    const sf = fSerieFolio.trim().toLowerCase();
    const enRango = (v: string | null, desde: string, hasta: string) => {
      if (!desde && !hasta) return true;
      if (!v) return false;
      const d = v.slice(0, 10);
      return (!desde || d >= desde) && (!hasta || d <= hasta);
    };
    return facturas.filter((f) => {
      if (b && ![`#${f.id}`, f.emisor_nombre, f.emisor_rfc, f.emisor_tax_id, f.emisor_pais, f.folio, f.serie, f.uuid, f.descripcion]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(b))) return false;
      if (!enRango(f.fecha, fEmiDesde, fEmiHasta)) return false;
      if (fEmisor && f.emisor_nombre !== fEmisor) return false;
      if (sf) {
        const serieFolio = [f.serie, f.folio].filter(Boolean).join("-").toLowerCase();
        if (!serieFolio.includes(sf) && !(f.folio ?? "").toLowerCase().includes(sf)) return false;
      }
      if (fMoneda && f.moneda !== fMoneda) return false;
      if (!enRango(f.pagada_en ?? f.fecha_pago_programada ?? null, fPagoDesde, fPagoHasta)) return false;
      return true;
    });
  }, [facturas, busqueda, fEmiDesde, fEmiHasta, fEmisor, fSerieFolio, fMoneda, fPagoDesde, fPagoHasta]);

  // Orden de la tabla (clic en los encabezados). `null` respeta el orden que
  // manda el backend (fecha del CFDI descendente).
  const [sort, setSort] = useState<{ key: SortKeyRec; dir: "asc" | "desc" } | null>(null);
  const ordenadas = useMemo(() => {
    if (!sort) return visibles;
    const txt = (v: string | null | undefined) => (v ?? "").toLowerCase();
    const serieFolio = (f: FacturaRecibidaItem) =>
      [f.serie, f.folio].filter(Boolean).join("-");
    // La columna Pago ordena por la fecha que manda: la real si ya se pagó, si
    // no la programada. Las facturas sin ninguna quedan al final.
    const pago = (f: FacturaRecibidaItem) => f.pagada_en ?? f.fecha_pago_programada ?? null;
    const arr = [...visibles];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "id": cmp = a.id - b.id; break;
        case "fecha": cmp = txt(a.fecha).localeCompare(txt(b.fecha)); break;
        case "emisor_nombre":
          cmp = txt(a.emisor_nombre).localeCompare(txt(b.emisor_nombre), "es"); break;
        case "folio":
          cmp = serieFolio(a).localeCompare(serieFolio(b), "es", { numeric: true }); break;
        case "origen": cmp = a.origen.localeCompare(b.origen); break;
        case "subtotal": cmp = (a.subtotal ?? 0) - (b.subtotal ?? 0); break;
        case "total": cmp = (a.total ?? 0) - (b.total ?? 0); break;
        case "moneda": cmp = txt(a.moneda).localeCompare(txt(b.moneda)); break;
        case "pago": {
          const x = pago(a), y = pago(b);
          if (!x && !y) cmp = 0;
          else if (!x) return 1;    // sin fecha de pago: siempre al final
          else if (!y) return -1;
          else cmp = x.localeCompare(y);
          break;
        }
        case "subido_en": cmp = a.subido_en.localeCompare(b.subido_en); break;
      }
      if (cmp === 0) cmp = a.id - b.id;  // desempate estable
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [visibles, sort]);

  const [abiertas, setAbiertas] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setAbiertas((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const borrar = useMutation({
    mutationFn: (id: number) => borrarFacturaRecibida(id),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["facturas-recibidas"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar la factura."),
  });

  const descargar = async (id: number, tipo: "pdf" | "xml") => {
    setError(null);
    try {
      await descargarFacturaRecibidaArchivo(id, tipo);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Error al descargar el ${tipo.toUpperCase()}`);
    }
  };

  const descargarFsp = async (id: number) => {
    setError(null);
    try {
      await descargarFacturaRecibidaFsp(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al generar el formato FSP");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Facturas Recibidas</h1>
        <p className="text-sm text-slate-500 mt-1">
          Carga aquí las facturas de proveedores y contrapartes. Con CFDI: el PDF junto con su XML
          del mismo nombre y los datos se leen del XML. Sin CFDI (emitidas en el extranjero): captura
          manual con el PDF de la factura.
        </p>
      </div>

      <div className="bg-white border rounded-lg">
        <div className="flex border-b text-sm">
          {(["cfdi", "extranjera"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setModo(m)}
              className={`px-4 py-2.5 -mb-px border-b-2 ${
                modo === m
                  ? "border-blue-600 text-blue-700 font-medium"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {m === "cfdi" ? "Carga de CFDI (PDF + XML)" : "Factura extranjera (captura manual + PDF)"}
            </button>
          ))}
        </div>
        <div className="p-4">
          {modo === "cfdi" ? (
            <FormularioCarga
              onCargado={() => qc.invalidateQueries({ queryKey: ["facturas-recibidas"] })}
            />
          ) : (
            <FacturaExtranjeraForm
              onCargada={() => qc.invalidateQueries({ queryKey: ["facturas-recibidas"] })}
            />
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          No se pudieron cargar las facturas: {(q.error as Error).message}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b bg-slate-50 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-slate-700">
            {facturas.length} factura{facturas.length === 1 ? "" : "s"} cargada
            {facturas.length === 1 ? "" : "s"}
          </span>
          {(hayFiltros || busqueda.trim()) && visibles.length !== facturas.length && (
            <span className="text-xs text-slate-500">· {visibles.length} visible{visibles.length === 1 ? "" : "s"}</span>
          )}
          <input
            type="text"
            placeholder="Buscar por emisor, folio, UUID…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="ml-auto border rounded px-2 py-1 text-sm w-64"
          />
        </div>
        <div className="px-4 py-2 border-b bg-white flex flex-wrap items-end gap-x-4 gap-y-2 text-xs text-slate-600">
          <FiltroCampo label="Fecha de emisión">
            <input type="date" value={fEmiDesde} onChange={(e) => setFEmiDesde(e.target.value)} className="border rounded px-1.5 py-1 text-xs" title="Desde" />
            <span className="text-slate-400">–</span>
            <input type="date" value={fEmiHasta} onChange={(e) => setFEmiHasta(e.target.value)} className="border rounded px-1.5 py-1 text-xs" title="Hasta" />
          </FiltroCampo>
          <FiltroCampo label="Emisor">
            <select value={fEmisor} onChange={(e) => setFEmisor(e.target.value)} className="border rounded px-1.5 py-1 text-xs max-w-56">
              <option value="">Todos</option>
              {emisores.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </FiltroCampo>
          <FiltroCampo label="Serie-Folio">
            <input type="text" value={fSerieFolio} onChange={(e) => setFSerieFolio(e.target.value)} placeholder="p. ej. A-123" className="border rounded px-1.5 py-1 text-xs w-28" />
          </FiltroCampo>
          <FiltroCampo label="Moneda">
            <select value={fMoneda} onChange={(e) => setFMoneda(e.target.value)} className="border rounded px-1.5 py-1 text-xs">
              <option value="">Todas</option>
              {monedas.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </FiltroCampo>
          <FiltroCampo label="Fecha de pago" title="Fecha real de pago; si aún no se ha pagado, la programada">
            <input type="date" value={fPagoDesde} onChange={(e) => setFPagoDesde(e.target.value)} className="border rounded px-1.5 py-1 text-xs" title="Desde" />
            <span className="text-slate-400">–</span>
            <input type="date" value={fPagoHasta} onChange={(e) => setFPagoHasta(e.target.value)} className="border rounded px-1.5 py-1 text-xs" title="Hasta" />
          </FiltroCampo>
          {hayFiltros && (
            <button type="button" onClick={limpiarFiltros} className="ml-auto text-xs text-blue-700 hover:underline pb-1">
              Limpiar filtros
            </button>
          )}
        </div>
        {q.isLoading ? (
          <div className="p-4 text-sm text-slate-500">Cargando…</div>
        ) : visibles.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            {facturas.length === 0
              ? "Aún no hay facturas cargadas. Sube la primera con el formulario de arriba."
              : "Ninguna factura coincide con la búsqueda o los filtros."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="px-3 py-2 w-8" />
                  <ThRec label="# Factura" k="id" sort={sort} setSort={setSort} align="right" />
                  <ThRec label="Fecha" k="fecha" sort={sort} setSort={setSort} />
                  <ThRec label="Emisor" k="emisor_nombre" sort={sort} setSort={setSort} />
                  <ThRec label="Serie-Folio" k="folio" sort={sort} setSort={setSort} />
                  <ThRec label="Origen" k="origen" sort={sort} setSort={setSort} />
                  <ThRec label="Subtotal" k="subtotal" sort={sort} setSort={setSort} align="right" />
                  <ThRec label="Total" k="total" sort={sort} setSort={setSort} align="right" />
                  <ThRec label="Moneda" k="moneda" sort={sort} setSort={setSort} />
                  <ThRec label="Pago" k="pago" sort={sort} setSort={setSort} />
                  <ThRec label="Subida" k="subido_en" sort={sort} setSort={setSort} />
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ordenadas.map((f) => (
                  <FilaFactura
                    key={f.id}
                    f={f}
                    abierta={abiertas.has(f.id)}
                    onToggle={() => toggle(f.id)}
                    onDescargar={descargar}
                    onDescargarFsp={descargarFsp}
                    onError={setError}
                    onBorrar={(id) => {
                      if (
                        confirm(
                          `¿Borrar la factura #${id} (${f.emisor_nombre ?? "sin emisor"}, ${money(f.total, f.moneda)})?\n\nSe eliminan también sus archivos. Esta acción no se puede deshacer.`,
                        )
                      ) {
                        borrar.mutate(id);
                      }
                    }}
                    borrando={borrar.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Formulario de carga ----------------------------------------------

type ResultadoCarga = { nombre: string; ok: boolean; detalle: string };

function FormularioCarga({ onCargado }: { onCargado: () => void }) {
  const archivosRef = useRef<HTMLInputElement>(null);
  const [seleccion, setSeleccion] = useState<number>(0);
  const [descripcion, setDescripcion] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState<string | null>(null);
  const [resultados, setResultados] = useState<ResultadoCarga[] | null>(null);

  const onSeleccion = () => {
    setResultados(null);
    setSeleccion(archivosRef.current?.files?.length ?? 0);
  };

  // Empareja los archivos seleccionados por nombre base: cada PDF con su XML.
  // Devuelve los pares completos y, como errores, los archivos sin su pareja.
  const emparejar = (files: File[]) => {
    const base = (n: string) => n.replace(/\.(pdf|xml)$/i, "").toLowerCase();
    const pdfs = new Map(files.filter((f) => /\.pdf$/i.test(f.name)).map((f) => [base(f.name), f]));
    const xmls = new Map(files.filter((f) => /\.xml$/i.test(f.name)).map((f) => [base(f.name), f]));
    const otros = files.filter((f) => !/\.(pdf|xml)$/i.test(f.name));

    const pares: { nombre: string; pdf: File; xml: File }[] = [];
    const sueltos: ResultadoCarga[] = otros.map((f) => ({
      nombre: f.name, ok: false, detalle: "Solo se aceptan archivos .pdf y .xml.",
    }));
    for (const [b, pdf] of pdfs) {
      const xml = xmls.get(b);
      if (xml) {
        pares.push({ nombre: pdf.name, pdf, xml });
        xmls.delete(b);
      } else {
        sueltos.push({
          nombre: pdf.name, ok: false,
          detalle: `Falta su XML (${pdf.name.replace(/\.pdf$/i, ".xml")}) — selecciónalos juntos.`,
        });
      }
    }
    for (const xml of xmls.values()) {
      sueltos.push({
        nombre: xml.name, ok: false,
        detalle: `Falta su PDF (${xml.name.replace(/\.xml$/i, ".pdf")}) — selecciónalos juntos.`,
      });
    }
    return { pares, sueltos };
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const files = Array.from(archivosRef.current?.files ?? []);
    if (!files.length) {
      setResultados([{ nombre: "—", ok: false, detalle: "Selecciona los archivos de las facturas (PDF + XML)." }]);
      return;
    }
    const { pares, sueltos } = emparejar(files);
    const res: ResultadoCarga[] = [...sueltos];

    setSubiendo(true);
    try {
      for (const [i, par] of pares.entries()) {
        setProgreso(`Subiendo ${i + 1} de ${pares.length}: ${par.nombre}…`);
        const fd = new FormData();
        fd.append("pdf", par.pdf);
        fd.append("xml", par.xml);
        if (descripcion.trim()) fd.append("descripcion", descripcion.trim());
        try {
          const f = await subirFacturaRecibida(fd);
          res.push({
            nombre: par.nombre, ok: true,
            detalle: `#${f.id} · ${f.emisor_nombre ?? "—"} · ${money(f.total, f.moneda)}`,
          });
        } catch (err) {
          res.push({
            nombre: par.nombre, ok: false,
            detalle: err instanceof Error ? err.message : "Error al subir.",
          });
        }
      }
    } finally {
      setSubiendo(false);
      setProgreso(null);
    }

    setResultados(res);
    if (res.some((r) => r.ok)) {
      if (archivosRef.current) archivosRef.current.value = "";
      setSeleccion(0);
      setDescripcion("");
      onCargado();
    }
  };

  const okCount = resultados?.filter((r) => r.ok).length ?? 0;

  return (
    <form onSubmit={onSubmit} className="bg-white border rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className="block text-xs text-slate-500 mb-1">
            Facturas — selecciona los PDF junto con sus XML (mismo nombre); puedes subir varias a la vez
          </span>
          <input
            ref={archivosRef}
            type="file"
            multiple
            accept=".pdf,.xml,application/pdf,text/xml,application/xml"
            onChange={onSeleccion}
            className="block text-sm text-slate-600 file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-sm file:text-blue-700 hover:file:bg-blue-100"
          />
          {seleccion > 0 && (
            <span className="block text-xs text-slate-400 mt-1">
              {seleccion} archivo{seleccion === 1 ? "" : "s"} seleccionado{seleccion === 1 ? "" : "s"}
            </span>
          )}
        </label>
        <button
          type="submit"
          disabled={subiendo}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 ml-auto"
        >
          <Upload className="w-4 h-4" /> {subiendo ? "Subiendo…" : "Subir facturas"}
        </button>
      </div>

      <label className="block text-sm">
        <span className="block text-xs text-slate-500 mb-1">
          Notas / descripción (opcional, se aplica a todas las de esta carga)
        </span>
        <input
          type="text"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="p. ej. Facturas de energía junio"
          className="border rounded px-2 py-1.5 text-sm w-full max-w-2xl"
        />
      </label>

      {progreso && <div className="text-sm text-slate-500">{progreso}</div>}

      {resultados && (
        <div className="border rounded divide-y text-sm">
          <div className="px-3 py-2 bg-slate-50 text-xs text-slate-600">
            {okCount} de {resultados.length} cargada{okCount === 1 ? "" : "s"} correctamente
          </div>
          {resultados.map((r, i) => (
            <div key={i} className={`px-3 py-1.5 flex gap-3 ${r.ok ? "" : "bg-red-50/50"}`}>
              <span className={`shrink-0 ${r.ok ? "text-emerald-600" : "text-red-600"}`}>
                {r.ok ? "✓" : "✗"}
              </span>
              <span className="font-medium shrink-0">{r.nombre}</span>
              <span className={r.ok ? "text-slate-500" : "text-red-700"}>{r.detalle}</span>
            </div>
          ))}
        </div>
      )}
    </form>
  );
}

// ---------- Fila + detalle ----------------------------------------------------

function FilaFactura({
  f,
  abierta,
  onToggle,
  onDescargar,
  onDescargarFsp,
  onBorrar,
  onError,
  borrando,
}: {
  f: FacturaRecibidaItem;
  abierta: boolean;
  onToggle: () => void;
  onDescargar: (id: number, tipo: "pdf" | "xml") => void;
  onDescargarFsp: (id: number) => void;
  onBorrar: (id: number) => void;
  onError: (msg: string) => void;
  borrando: boolean;
}) {
  return (
    <>
      <tr className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={onToggle}>
        <td className="px-3 py-2 text-slate-400">
          <ChevronRight
            className={`w-3.5 h-3.5 transition-transform ${abierta ? "rotate-90" : ""}`}
          />
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-slate-500">#{f.id}</td>
        <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(f.fecha)}</td>
        <td className="px-3 py-2">
          <div className="font-medium">{f.emisor_nombre ?? "—"}</div>
          {f.emisor_rfc && <div className="text-xs text-slate-400">{f.emisor_rfc}</div>}
          {f.origen === "extranjera" && (
            <div className="text-xs text-slate-400">
              {[f.emisor_pais, f.emisor_tax_id && `Tax ID ${f.emisor_tax_id}`].filter(Boolean).join(" · ")}
            </div>
          )}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {[f.serie, f.folio].filter(Boolean).join("-") || "—"}
        </td>
        <td className="px-3 py-2">
          {f.origen === "cfdi" ? (
            <span className="inline-block rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 text-xs">
              CFDI {f.version_cfdi ?? ""}
            </span>
          ) : f.origen === "extranjera" ? (
            <span className="inline-block rounded bg-teal-50 text-teal-700 px-1.5 py-0.5 text-xs">
              Extranjera
            </span>
          ) : (
            <span className="inline-block rounded bg-amber-50 text-amber-700 px-1.5 py-0.5 text-xs">
              Manual
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{money(f.subtotal, f.moneda)}</td>
        <td className="px-3 py-2 text-right tabular-nums font-medium">
          {money(f.total, f.moneda)}
        </td>
        <td className="px-3 py-2">{f.moneda ?? "—"}</td>
        <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <CeldaPago f={f} onError={onError} />
        </td>
        <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
          {new Date(f.subido_en).toLocaleDateString("es-MX")}
          {f.subido_por ? ` · ${f.subido_por.split("@")[0]}` : ""}
        </td>
        <td className="px-3 py-2">
          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              disabled={!f.tiene_pdf}
              title={f.tiene_pdf ? "Descargar PDF" : "Sin PDF"}
              onClick={() => onDescargar(f.id, "pdf")}
              className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileDown className="w-3.5 h-3.5" /> PDF
            </button>
            <button
              type="button"
              disabled={!f.tiene_xml}
              title={f.tiene_xml ? "Descargar XML" : "Sin XML"}
              onClick={() => onDescargar(f.id, "xml")}
              className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileCode className="w-3.5 h-3.5" /> XML
            </button>
            <button
              type="button"
              title="Descargar formato FSP (Solicitud de Pago) llenado con esta factura"
              onClick={() => onDescargarFsp(f.id)}
              className="inline-flex items-center gap-1 text-xs border border-emerald-300 rounded px-2 py-1 text-emerald-700 hover:bg-emerald-50"
            >
              <FileText className="w-3.5 h-3.5" /> FSP
            </button>
            <button
              type="button"
              title="Borrar factura"
              disabled={borrando}
              onClick={() => onBorrar(f.id)}
              className="text-slate-400 hover:text-red-600 disabled:opacity-50 px-1"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      {abierta && (
        <tr className="bg-slate-50/60 border-b last:border-0">
          <td colSpan={12} className="px-6 py-3">
            <DetalleFactura f={f} />
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- Seguimiento de pago -----------------------------------------------
// Fecha programada (input) + marcar pagada. Colores contra HOY: rojo = vencida,
// ámbar = vence en ≤7 días. Pagada = badge verde (clic para revertir).

type SortKeyRec =
  | "id"
  | "fecha" | "emisor_nombre" | "folio" | "origen"
  | "subtotal" | "total" | "moneda" | "pago" | "subido_en";

/** Encabezado clicable: ordena por su clave y alterna asc/desc si ya está activo. */
function FiltroCampo({ label, title, children }: { label: string; title?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5" title={title}>
      <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
      <span className="flex items-center gap-1">{children}</span>
    </label>
  );
}

function ThRec({ label, k, sort, setSort, align }: {
  label: string;
  k: SortKeyRec;
  sort: { key: SortKeyRec; dir: "asc" | "desc" } | null;
  setSort: (s: { key: SortKeyRec; dir: "asc" | "desc" }) => void;
  align?: "right";
}) {
  const activo = sort?.key === k;
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() =>
          setSort({ key: k, dir: activo && sort!.dir === "asc" ? "desc" : "asc" })
        }
        className={`inline-flex items-center gap-1 hover:text-slate-700 ${
          activo ? "text-slate-700 font-medium" : ""
        }`}
      >
        {label}
        <span className="text-[10px]">{activo ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

function CeldaPago({ f, onError }: { f: FacturaRecibidaItem; onError: (msg: string) => void }) {
  const qc = useQueryClient();
  const invalidar = () => qc.invalidateQueries({ queryKey: ["facturas-recibidas"] });

  const programar = useMutation({
    mutationFn: (fecha: string | null) => programarPagoFacturaRecibida(f.id, fecha),
    onSuccess: invalidar,
    onError: (e) =>
      onError(e instanceof Error ? e.message : "No se pudo guardar la fecha de pago."),
  });
  const marcar = useMutation({
    mutationFn: (pagada: boolean) => marcarPagadaFacturaRecibida(f.id, pagada),
    onSuccess: invalidar,
    onError: (e) =>
      onError(e instanceof Error ? e.message : "No se pudo actualizar el estado de pago."),
  });

  if (f.pagada_en) {
    return (
      <button
        type="button"
        disabled={marcar.isPending}
        title={`Pagada${f.pagada_por ? ` por ${f.pagada_por.split("@")[0]}` : ""} — clic para revertir`}
        onClick={() => {
          if (confirm(`¿Quitar la marca de PAGADA de la factura #${f.id}?`)) marcar.mutate(false);
        }}
        className="inline-flex items-center gap-1 rounded bg-emerald-50 text-emerald-700 px-1.5 py-0.5 text-xs hover:bg-emerald-100 disabled:opacity-50"
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> {fechaCorta(f.pagada_en)}
      </button>
    );
  }

  const hoy = new Date().toISOString().slice(0, 10);
  const en7 = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  const prog = f.fecha_pago_programada;
  const tono = !prog
    ? "border-slate-200 text-slate-500"
    : prog < hoy
      ? "border-red-400 bg-red-50 text-red-700 font-medium"
      : prog <= en7
        ? "border-amber-400 bg-amber-50 text-amber-800"
        : "border-slate-300";
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="date"
        value={prog ?? ""}
        disabled={programar.isPending}
        title={
          !prog
            ? "Fecha programada de pago (Tabla de la OC o acuerdo con el proveedor)"
            : prog < hoy
              ? "Pago VENCIDO"
              : prog <= en7
                ? "Vence en 7 días o menos"
                : "Fecha programada de pago"
        }
        onChange={(e) => programar.mutate(e.target.value || null)}
        className={`border rounded px-1.5 py-0.5 text-xs w-[8.4rem] ${tono}`}
      />
      <button
        type="button"
        disabled={marcar.isPending}
        title="Marcar como pagada (con fecha de hoy)"
        onClick={() => {
          if (confirm(`¿Marcar como PAGADA la factura #${f.id} (${f.emisor_nombre ?? "—"})?`))
            marcar.mutate(true);
        }}
        className="text-slate-400 hover:text-emerald-600 disabled:opacity-50"
      >
        <CheckCircle2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function DetalleFactura({ f }: { f: FacturaRecibidaItem }) {
  const q = useQuery({
    queryKey: ["factura-recibida-conceptos", f.id],
    queryFn: () => getFacturaRecibidaConceptos(f.id),
    enabled: f.origen === "cfdi" || f.origen === "extranjera",
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-3 max-w-5xl">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-0.5 text-xs">
        {f.origen === "extranjera" ? (
          <>
            <Linea k="País del emisor" v={f.emisor_pais} />
            <Linea k="Tax ID / VAT" v={f.emisor_tax_id} />
            <Linea k="Receptor" v={f.receptor_nombre && `${f.receptor_nombre} (${f.receptor_rfc ?? ""})`} />
            <Linea k="Fecha de la factura" v={f.fecha?.slice(0, 10)} />
            <Linea k="Condiciones de pago" v={f.forma_pago} />
          </>
        ) : (
          <>
            <Linea k="UUID (timbre fiscal)" v={f.uuid} />
            <Linea k="Receptor" v={f.receptor_nombre && `${f.receptor_nombre} (${f.receptor_rfc ?? ""})`} />
            <Linea k="Uso CFDI" v={f.receptor_uso_cfdi} />
            <Linea k="Fecha del CFDI" v={f.fecha?.replace("T", " ")} />
            <Linea k="Timbrado" v={f.fecha_timbrado?.replace("T", " ")} />
            <Linea k="Método / forma de pago" v={[f.metodo_pago, f.forma_pago].filter(Boolean).join(" / ")} />
          </>
        )}
        <Linea k="Tipo de cambio" v={f.tipo_cambio != null ? num(f.tipo_cambio, 4) : null} />
        <Linea k={f.origen === "extranjera" ? "Impuestos" : "Impuestos trasladados"} v={f.total_impuestos != null ? money(f.total_impuestos, f.moneda) : null} />
        <Linea k="Archivos" v={[f.nombre_archivo_xml, f.nombre_archivo_pdf].filter(Boolean).join(" · ")} />
        <Linea k="Pago programado" v={f.fecha_pago_programada} />
        <Linea
          k="Pagada"
          v={f.pagada_en && `${f.pagada_en}${f.pagada_por ? ` · ${f.pagada_por.split("@")[0]}` : ""}`}
        />
      </div>
      {f.descripcion && (
        <div className="text-xs text-slate-600">
          <span className="text-slate-400">Notas:</span> {f.descripcion}
        </div>
      )}

      {(f.origen === "cfdi" || f.origen === "extranjera") && (
        <div>
          <div className="text-xs font-medium text-slate-600 mb-1">{f.origen === "cfdi" ? "Conceptos del CFDI" : "Conceptos"}</div>
          {q.isLoading && <div className="text-xs text-slate-400">Cargando conceptos…</div>}
          {q.isError && (
            <div className="text-xs text-red-600">
              No se pudieron cargar los conceptos: {(q.error as Error).message}
            </div>
          )}
          {q.data &&
            (q.data.length === 0 ? (
              <div className="text-xs text-slate-400">Sin conceptos.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="text-xs border rounded bg-white min-w-[34rem]">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="px-2.5 py-1">#</th>
                      <th className="px-2.5 py-1">Clave</th>
                      <th className="px-2.5 py-1">Descripción</th>
                      <th className="px-2.5 py-1 text-right">Cantidad</th>
                      <th className="px-2.5 py-1">Unidad</th>
                      <th className="px-2.5 py-1 text-right">P. unitario</th>
                      <th className="px-2.5 py-1 text-right">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.map((c) => (
                      <tr key={c.num_lin} className="border-b last:border-0">
                        <td className="px-2.5 py-1 text-slate-500">{c.num_lin}</td>
                        <td className="px-2.5 py-1 whitespace-nowrap">{c.clave_prod_serv ?? "—"}</td>
                        <td className="px-2.5 py-1">{c.descripcion ?? "—"}</td>
                        <td className="px-2.5 py-1 text-right tabular-nums">{num(c.cantidad, 3)}</td>
                        <td className="px-2.5 py-1">{c.unidad ?? "—"}</td>
                        <td className="px-2.5 py-1 text-right tabular-nums">{num(c.valor_unitario, 4)}</td>
                        <td className="px-2.5 py-1 text-right tabular-nums font-medium">
                          {money(c.importe, f.moneda)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function Linea({ k, v }: { k: string; v: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-0.5">
      <span className="text-slate-500 whitespace-nowrap">{k}</span>
      <span className="font-medium text-slate-700 text-right break-all">{v || "—"}</span>
    </div>
  );
}
